import { Divider, Tooltip } from "@mui/material";
import moment from "moment";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Formater, TypeRecords } from "src/framework/commons/Formater";
import { StringUtil } from "src/framework/commons/StringUtil";
import { FullWidthButton } from "src/framework/components/buttons/FullWidthButton";
import { BottomBar } from "src/framework/components/commons/BottomBar";
import { useObservable } from "src/framework/store/storeUtil";
import { getLimitacoesProjetos, grupoEstrutura$, limitacoes$ } from "src/store/global.store";
import { TIPO_SERVICO_REVENDA } from "./constants";
import { GrupoEstrutura, LimitacaoProjeto } from "./types";
import { mostrarAvisoServicoRemovido } from "./utils/mostrarAvisoServicoRemovido";
import { removeServicoCamposProjeto } from "./utils/removeServicoCamposProjeto";
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { InputCurrency } from "src/framework/components/forms/inputs/InputCurrency";
import { standAlonePage$ } from "src/framework/store/ApiEvents";

export interface Revisao {
    codigo_venda?: string;
    projeto_id: number;
    limitacao_projeto_id: number;
    limitacao_observacao: string;
    observacao: string;
    disposicoes: GrupoEstrutura[];
    limitacao_projeto?: LimitacaoProjeto;
    valor_servico?: number | null;
}

interface RevisaoProjetoProperties {
    projeto: any;
    onExit: () => void;
    onSave: (projeto: any, revisao?: any) => void;
}

const DEFAULT_REVISAO = {
    projeto_id: 0,
    disposicoes: [],
    limitacao_observacao: "",
    observacao: "",
    limitacao_projeto_id: 0,
    valor_servico: undefined
}

const RevisaoProjeto: FC<RevisaoProjetoProperties> = ({
    projeto: _projeto,
    onExit,
    onSave
}) => {
    const standAlone = useObservable(standAlonePage$);
    const grupoEstruturaState = useObservable(grupoEstrutura$);
    const limitacoesProjetoState = useObservable(limitacoes$);
    const [projeto, setProjeto] = useState<any>(null);
    const [revisao, setRevisao] = useState<Revisao>(DEFAULT_REVISAO);
    const [limitacaoProjeto, setLimitacaoProjeto] = useState<Revisao | null>(null);

    useEffect(()=>{
        getLimitacoesProjetos()
    },[])

    useEffect(() => {
        if (_projeto) {
            let projetoLocal = { ..._projeto }
            if (projetoLocal.servico === TIPO_SERVICO_REVENDA && Number(projetoLocal.valor_servico || 0) > 0 && projetoLocal.revenda && !projetoLocal?.revenda?.segmentos_servico?.includes(projetoLocal?.segmento?.includes("on") ? "on" : "off")) {
                projetoLocal = removeServicoCamposProjeto(projetoLocal)
                projetoLocal.remove_servico = true;
                mostrarAvisoServicoRemovido()
            }
            setProjeto({ ...projetoLocal })
        }
        setRevisao(DEFAULT_REVISAO)

    }, [_projeto])

    const renderNomeGruposEstrutura = (): ReactNode => {
        const gruposIds = projeto?.disposicoes?.map((d: any) => d.grupo_estrutura_id);

        if (gruposIds?.length === 0) {
            return <></>
        }

        const disposicoes: GrupoEstrutura[] = grupoEstruturaState?.data?.filter((g: GrupoEstrutura) => gruposIds?.includes(g.id))?.map((g: any) => g.nome) || [];

        return <>{"Atual: " + disposicoes?.join(", ")}</>

    }

    const currentLimitacao = useMemo(() => {
        return limitacoesProjetoState?.data?.find((item: LimitacaoProjeto) => item.id === projeto?.solicitacao?.limitacao_projeto_id)?.descricao || ''
    } ,[limitacoesProjetoState, projeto])

    return <>
        <section style={{ paddingTop: "7px" }}>
            <div className="row">
                <div className="X12">
                    <h4>Revisão do projeto ({_projeto?.codigo_venda})</h4>
                </div>
            </div>

            <div className="row">
                <div className="X12">
                    <InputComboBoxList
                        multiple
                        limitTags={3}
                        label={"Grupos estrutura"}
                        textField="nome"
                        valueField="id"
                        options={grupoEstruturaState?.data?.filter((d: any) => d.vertical === true) || []}
                        value={revisao?.disposicoes}
                        autoComplete
                        helperText={renderNomeGruposEstrutura()}
                        onChange={(value: GrupoEstrutura[]) => {
                            setRevisao({ ...revisao, disposicoes: value })
                        }}
                        renderOptions={(option: any) => {
                            return `${option.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option.nome}`
                        }}
                    />
                </div>
            </div>
            <div className="row">
                <div className="X12" style={{ textAlign: "left", paddingTop: "10px", paddingLeft: "10px" }}>
                    <Divider />
                </div>
            </div>
            <div className="row">
                <div className="X12">
                    <InputComboBoxList
                        label={"Limitações do projeto"}
                        textField="descricao"
                        valueField="id"
                        options={limitacoesProjetoState?.data || []}
                        value={limitacaoProjeto}
                        autoComplete
                        helperText={"Atual: " + currentLimitacao}
                        onChange={(value: any) => {
                            const revisaoState: any = { ...revisao }
                            if (value) {
                                setLimitacaoProjeto(value)
                                revisaoState.limitacao_projeto_id = value.id
                                revisaoState.limitacao_projeto = value
                            } else {
                                setLimitacaoProjeto(null)
                                revisaoState.limitacao_projeto_id = 0
                                revisaoState.limitacao_projeto = null
                            }
                            setRevisao(revisaoState)
                        }}
                        renderOptions={(option: any) => {
                            return `${option?.descricao}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.descricao}`
                        }}


                    />
                </div>

                <div className="X12">
                    <InputText label="Observações da limitação" multiline maxRows={7} minRows={7}
                        InputProps={{
                            rows: 7
                        }}
                        fullWidth
                        value={revisao.limitacao_observacao}
                        onChange={(e: any) => {
                            const revisaoState: any = { ...revisao }
                            revisaoState.limitacao_observacao = e.target.value;
                            setRevisao(revisaoState)
                        }}
                        helperText={<><Tooltip title={projeto?.solicitacao?.limitacao_observacao}><span>{"Atual: " + StringUtil.truncate(projeto?.solicitacao?.limitacao_observacao, 30)}</span></Tooltip></>}
                    />
                </div>

            </div>

            <div className="row">
                <div className="X12" style={{ textAlign: "left", paddingTop: "10px", paddingLeft: "10px" }}>
                    <Divider />
                </div>

                <div className="X12">
                    <InputText label="Observações validade da proposta" multiline maxRows={7} minRows={7}
                        InputProps={{
                            rows: 7
                        }}
                        fullWidth
                        value={revisao.observacao}
                        onChange={(e: any) => {
                            const revisaoState: any = { ...revisao }
                            revisaoState.observacao = e.target.value;
                            setRevisao(revisaoState)
                        }}
                    // helperText={<><Tooltip title={projeto?.solicitacao?.observacao}><span>{"Atual: " + StringUtil.truncate(projeto?.solicitacao?.limitacao_observacao, 30)}</span></Tooltip></>}
                    />
                </div>
            </div>

            {projeto?.servico === TIPO_SERVICO_REVENDA && (
                <>
                    <div className="row">

                        <div className="X12" style={{ textAlign: "left", paddingTop: "10px", paddingLeft: "10px" }}>
                            <Divider />
                        </div>
                    </div>


                    <div className="row" >
                        <div className="X12 S6 M4">
                            <InputCurrency
                                label="Valor serviço líquido"
                                margin="normal"
                                value={revisao?.valor_servico}
                                // disabled={projeto?.servico !== TIPO_SERVICO_REVENDA}
                                onChangeValue={(newValue) => {
                                    const { value } = newValue
                                    const revisaoState: any = { ...revisao };
                                    revisaoState.valor_servico = value;
                                    setRevisao(revisaoState)
                                    // onChangeValue(state)
                                }}
                                helperText={"Atual: " + Formater.toString(projeto.valor_servico, TypeRecords.CURRENCY)}
                            />
                        </div>
                    </div>
                </>
            )}
        </section>

        <BottomBar>
            {standAlone ? "" : (
                <FullWidthButton
                    text={"Cancelar"}
                    color={"secondary"}
                    onClick={() => {
                        onExit();
                    }}
                />
            )}

            <FullWidthButton
                disableElevation={true}
                text={"Salvar"}
                // disabled={}
                color={"primary"}
                variant={"contained"}
                onClick={() => {
                    if (revisao) {
                        confirmationDialog.setup({
                            title: "Aviso!!!",
                            content: "Enviar revisão?",

                            onConfirmation: () => {
                                const _revisao = [];
                                const now = moment()

                                if (revisao.disposicoes.length > 0) {
                                    const parseRevisao = revisao.disposicoes.map((g: any) => g.nome).join(", ")
                                    _revisao.push(`Disposições: ${parseRevisao}`)
                                }

                                if (revisao.limitacao_projeto_id > 0) {
                                    const limitacaoIndex = limitacoesProjetoState.data.findIndex((l: any) => l.id === revisao.limitacao_projeto_id)
                                    if (limitacaoIndex > -1) {
                                        _revisao.push(`LIMITAÇÃO PROJETO: ${limitacoesProjetoState.data[limitacaoIndex].descricao}`)
                                    }

                                }
                                if (revisao.limitacao_observacao !== "") {
                                    _revisao.push(`OBSERVAÇÃO LIMITAÇÃO: ${revisao.limitacao_observacao}`)
                                }


                                if (revisao.observacao !== "") {
                                    _revisao.push(`OBSERVAÇÃO: ${revisao.observacao}`)
                                }


                                if (!!revisao?.valor_servico) {
                                    _revisao.push(`VALOR SERVIÇO LÍQUIDO: R$ ${revisao.valor_servico}`)
                                }

                                if (_revisao.length > 0) {
                                    projeto.distribuidor_id = projeto.distribuidor.id
                                    projeto.revisoes = [
                                        {
                                            descricao: _revisao.join("\n\r\n\r"),
                                            data_revisao: now.format("YYYY-MM-DD HH:mm:ss")
                                        }
                                    ]
                                    revisao.codigo_venda = projeto.codigo_venda
                                    onSave(projeto, revisao);
                                }

                                confirmationDialog.close()
                            },

                            onClose: () => {
                                confirmationDialog.close()
                            }
                        })

                        confirmationDialog.open();
                    }
                }}
            />

        </BottomBar>
    </>
}

export { RevisaoProjeto }